import { Component } from "react";

/**
 * Top-level error boundary. Without this, any render-time exception unmounts the
 * whole React tree and the user sees a blank white screen with no information.
 * Here we catch it, show the actual error + a recovery action, and log details
 * to the console so the root cause is visible instead of hidden.
 */
// A new deploy replaces the hashed JS chunk files on the server; a tab left open
// from before that deploy still references the old filenames, so its next lazy
// chunk load 404s with this error. It is not a real app bug — reloading the tab
// picks up the new build and fixes it.
//
// Guard against reloading forever: store *which* chunk-error message we last
// reloaded for. If the exact same error comes back after the reload, the
// problem wasn't a stale bundle (a reload would have fixed that) — it's
// persistent, so stop and show the normal error screen instead of looping.
// A later deploy produces a *different* chunk filename in the error message,
// so it still gets its own auto-reload. (An earlier version of this guard
// cleared on every mount, which cleared it before the async chunk import even
// had a chance to fail again — that reloaded in an infinite ~2s loop instead
// of ever reaching the "persistent" case.)
function isChunkLoadError(error) {
  const msg = String(error?.message || error || "");
  return error?.name === "ChunkLoadError" || /Loading (chunk|CSS chunk) .* failed/i.test(msg);
}

const RELOAD_GUARD_KEY = "traceqc:chunk-error-reloaded-for";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error("TraceQC UI crash:", error, info);

    if (isChunkLoadError(error)) {
      const signature = String(error?.message || error);
      if (sessionStorage.getItem(RELOAD_GUARD_KEY) !== signature) {
        sessionStorage.setItem(RELOAD_GUARD_KEY, signature);
        window.location.reload();
      }
    }
  }

  handleReset = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-50 px-4">
        <div className="w-full max-w-lg border border-rose-200 bg-white rounded-sm p-6 shadow-sm">
          <h1 className="font-display font-extrabold text-lg text-rose-700">Something went wrong</h1>
          <p className="text-sm text-zinc-600 mt-1">
            The page hit an unexpected error. Your data is safe — this is a display issue.
          </p>
          <pre className="mt-4 text-[11px] font-mono-data text-rose-800 bg-rose-50 border border-rose-100 rounded-sm p-3 overflow-auto max-h-48 whitespace-pre-wrap">
            {String(error?.message || error)}
            {info?.componentStack ? "\n" + info.componentStack : ""}
          </pre>
          <div className="flex gap-2 mt-4">
            <button
              onClick={this.handleReset}
              className="bg-zinc-900 hover:bg-zinc-700 text-white text-sm font-bold px-4 py-2 rounded-sm transition-colors"
            >
              Try again
            </button>
            <button
              onClick={() => { window.location.href = "/"; }}
              className="border border-zinc-300 hover:border-zinc-900 text-sm font-bold px-4 py-2 rounded-sm transition-colors"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
